import { useMemo } from 'react'
import { NetworkStatus } from 'apollo-client'
import { useListPickupStoreGroupsQuery } from 'domains/aggregate-store-performance/api'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { type StoreGroupDropdownOption } from './StoreGroupDropdown.types'
import { ALL_STORES_OPTION_ID } from './StoreGroupDropdown.constants'

const formatStoreGroupLabel = (name: string, numberOfStores: number): string =>
  `${name} (${numberOfStores})`

export const useGetStoreGroups = () => {
  const messages = useDomainMessages({
    label: 'aggregateStorePerformanceDomain.storeGroupDropdown.label',
    defaultPlaceholder: 'aggregateStorePerformanceDomain.storeGroupDropdown.defaultPlaceholder',
    groupsSelectedPlaceholder:
      'aggregateStorePerformanceDomain.storeGroupDropdown.groupsSelectedPlaceholder',
    allStores: 'aggregateStorePerformanceDomain.storeGroupDropdown.allStores',
  })

  const retailerId = useRetailerId()

  const { data, error, loading, refetch, networkStatus } = useListPickupStoreGroupsQuery({
    variables: {
      retailerId,
    },
  })

  const ALL_STORES_OPTION: StoreGroupDropdownOption = useMemo(() => {
    const allPickupRetailerLocations = (data && data.retailerLocationSearchArray) || []
    return {
      label: formatStoreGroupLabel(messages.allStores, allPickupRetailerLocations?.length ?? 0),
      name: messages.allStores,
      value: ALL_STORES_OPTION_ID,
      retailerLocationIds: allPickupRetailerLocations?.map(retailerLocation => retailerLocation.id),
    }
  }, [messages, data])

  const storeGroups = (data && data.pickupRetailerLocationGroup) || []

  const formattedStoreGroups: StoreGroupDropdownOption[] = storeGroups.map(storeGroup => {
    const retailerLocations = storeGroup.retailerLocations
    return {
      label: formatStoreGroupLabel(storeGroup.name, retailerLocations.length),
      name: storeGroup.name,
      value: storeGroup.id,
      retailerLocationIds: retailerLocations.map(retailerLocation => retailerLocation.id),
    }
  })

  const isLoading = loading || networkStatus === NetworkStatus.refetch

  return {
    storeGroups: [ALL_STORES_OPTION, ...formattedStoreGroups],
    loading: isLoading,
    error,
    refetch,
  }
}
